import type { ReactNode } from 'react'

import { useTranslation } from 'react-i18next'

import {
    flexRender,
    type Row,
} from '@tanstack/react-table'

import type { Selection } from 'react-aria-components'

import {
    Check,
    Eye,
    MousePointerClick,
} from 'lucide-react'

import { Button } from '../button'
import { EmptyState } from '../empty-state'
import { SkeletonTable } from '../skeleton-table'

import {
    DataTableScrollArea,
    SelectionCheckbox,
    SortableColumnLabel,
    StyledCell,
    StyledColumn,
    StyledRow,
    StyledTable,
    StyledTableBody,
    StyledTableHeader,
    type DataTableKeyOffset,
} from './data-table-primitives'

import { DataTablePagination } from './data-table-pagination'
import { DataTableToolbar } from './data-table-toolbar'
import { useDataTable } from './use-data-table'

import {
    DEFAULT_PAGE_SIZE_OPTIONS,
    type DataTableActionColumn,
    type DataTableColumnDef,
    type DataTablePaginationConfig,
} from './data-table-types'

import { cn } from '@/shared/lib/cn'

const ACTION_COLUMN_ID = '__actions__'

export interface DataTableProps<TData> {
    'aria-label': string

    columns: readonly DataTableColumnDef<TData>[]
    data: readonly TData[]
    getRowId: (row: TData) => string

    /**
     * Names the frozen identifying column.
     * Omitted → no frozen key column.
     */
    keyColumnId?: string

    actionColumn?: DataTableActionColumn<TData>

    search?: boolean
    toolbarContent?: ReactNode
    onExport?: () => void | Promise<void>

    pagination?: DataTablePaginationConfig

    isLoading?: boolean
    emptyState?: ReactNode

    minimumWidth?: string
    className?: string
}

export function DataTable<TData>({
    'aria-label': ariaLabel,
    columns,
    data,
    getRowId,
    keyColumnId,
    actionColumn,
    search = false,
    toolbarContent,
    onExport,
    pagination,
    isLoading = false,
    emptyState,
    minimumWidth,
    className,
}: DataTableProps<TData>) {
    const { t } = useTranslation()

    const {
        table,
        sortDescriptor,
        onSortChange,
        searchValue,
        onSearchChange,
        pageIndex,
        pageSize,
        pageCount,
        totalRowCount,
        setPageIndex,
        setPageSize,
    } = useDataTable({
        columns,
        data,
        getRowId,
        pagination,
        search,
    })

    const rows = table.getRowModel().rows

    const headers = table.getHeaderGroups()[0]?.headers ?? []

    const keyOffset: DataTableKeyOffset = actionColumn
        ? 'afterAction'
        : 'none'

    const rowHeaderId =
        keyColumnId ?? headers[0]?.column.id

    const isCheckboxMode =
        actionColumn?.mode === 'checkbox'

    function handleSelectionChange(
        selection: Selection,
    ): void {
        if (actionColumn?.mode !== 'checkbox') {
            return
        }

        const pageRowIds = rows.map((row) => row.id)

        /*
         * The table only modifies the selection of rows it
         * currently renders; selections made on other pages
         * are preserved (including through select-all and
         * deselect-all on the header checkbox).
         */
        const preservedIds =
            actionColumn.selectedRowIds.filter(
                (id) => !pageRowIds.includes(id),
            )

        const selectedPageIds =
            selection === 'all'
                ? pageRowIds
                : pageRowIds.filter((id) =>
                      selection.has(id),
                  )

        actionColumn.onSelectionChange([
            ...preservedIds,
            ...selectedPageIds,
        ])
    }

    function renderActionCell(row: Row<TData>): ReactNode {
        if (!actionColumn) {
            return null
        }

        if (actionColumn.mode === 'checkbox') {
            return <SelectionCheckbox />
        }

        const isSelected =
            actionColumn.mode === 'select' &&
            (actionColumn.isRowSelected?.(row.original) ??
                false)

        return (
            <Button
                variant={isSelected ? 'primary' : 'ghost'}
                size="medium"
                isIconOnly
                aria-label={actionColumn.getActionLabel(
                    row.original,
                )}
                onPress={() => {
                    actionColumn.onAction(row.original)
                }}
            >
                {actionColumn.mode === 'view' ? (
                    <Eye
                        aria-hidden="true"
                        className="h-4 w-4"
                    />
                ) : isSelected ? (
                    <Check
                        aria-hidden="true"
                        className="h-4 w-4"
                    />
                ) : (
                    <MousePointerClick
                        aria-hidden="true"
                        className="h-4 w-4"
                    />
                )}
            </Button>
        )
    }

    const showToolbar =
        search ||
        toolbarContent != null ||
        onExport !== undefined

    const showTable = !isLoading && rows.length > 0

    return (
        <div className={cn('space-y-4', className)}>
            {showToolbar ? (
                <DataTableToolbar
                    showSearch={search}
                    searchValue={searchValue}
                    onSearchChange={onSearchChange}
                    toolbarContent={toolbarContent}
                    onExport={onExport}
                />
            ) : null}

            {isLoading ? (
                <SkeletonTable
                    rows={pagination ? Math.min(pageSize, 8) : 6}
                    columns={
                        headers.length + (actionColumn ? 1 : 0)
                    }
                    loadingLabel={t('ui.loading.table')}
                />
            ) : null}

            {!isLoading && rows.length === 0
                ? (emptyState ?? (
                      <EmptyState
                          title={t('ui.dataTable.emptyTitle')}
                      />
                  ))
                : null}

            {showTable ? (
                <DataTableScrollArea
                    accessibleLabel={ariaLabel}
                >
                    <StyledTable
                        aria-label={ariaLabel}
                        minimumWidth={minimumWidth}
                        sortDescriptor={sortDescriptor}
                        onSortChange={onSortChange}
                        selectionMode={
                            isCheckboxMode
                                ? 'multiple'
                                : 'none'
                        }
                        selectionBehavior="toggle"
                        selectedKeys={
                            isCheckboxMode
                                ? new Set(
                                      actionColumn.selectedRowIds,
                                  )
                                : undefined
                        }
                        onSelectionChange={
                            isCheckboxMode
                                ? handleSelectionChange
                                : undefined
                        }
                    >
                        <StyledTableHeader>
                            {actionColumn ? (
                                <StyledColumn
                                    id={ACTION_COLUMN_ID}
                                    sticky="action"
                                    textValue={t(
                                        'ui.dataTable.actions',
                                    )}
                                >
                                    {isCheckboxMode ? (
                                        <SelectionCheckbox
                                            aria-label={t(
                                                'ui.dataTable.selectAll',
                                            )}
                                        />
                                    ) : (
                                        <span className="sr-only">
                                            {t(
                                                'ui.dataTable.actions',
                                            )}
                                        </span>
                                    )}
                                </StyledColumn>
                            ) : null}

                            {headers.map((header) => {
                                const column = header.column

                                const meta =
                                    column.columnDef.meta

                                return (
                                    <StyledColumn
                                        key={header.id}
                                        id={column.id}
                                        sticky={
                                            column.id ===
                                            keyColumnId
                                                ? 'key'
                                                : 'none'
                                        }
                                        keyOffset={keyOffset}
                                        isRowHeader={
                                            column.id ===
                                            rowHeaderId
                                        }
                                        allowsSorting={column.getCanSort()}
                                        className={
                                            meta?.headerClassName
                                        }
                                    >
                                        {({ sortDirection }) =>
                                            column.getCanSort() ? (
                                                <SortableColumnLabel
                                                    sortDirection={
                                                        sortDirection
                                                    }
                                                    align={
                                                        meta?.align
                                                    }
                                                >
                                                    {flexRender(
                                                        column
                                                            .columnDef
                                                            .header,
                                                        header.getContext(),
                                                    )}
                                                </SortableColumnLabel>
                                            ) : (
                                                flexRender(
                                                    column
                                                        .columnDef
                                                        .header,
                                                    header.getContext(),
                                                )
                                            )
                                        }
                                    </StyledColumn>
                                )
                            })}
                        </StyledTableHeader>

                        <StyledTableBody>
                            {rows.map((row) => (
                                <StyledRow
                                    key={row.id}
                                    id={row.id}
                                    textValue={row.id}
                                >
                                    {actionColumn ? (
                                        <StyledCell sticky="action">
                                            {renderActionCell(
                                                row,
                                            )}
                                        </StyledCell>
                                    ) : null}

                                    {row
                                        .getVisibleCells()
                                        .map((cell) => {
                                            const meta =
                                                cell.column
                                                    .columnDef
                                                    .meta

                                            return (
                                                <StyledCell
                                                    key={cell.id}
                                                    sticky={
                                                        cell.column
                                                            .id ===
                                                        keyColumnId
                                                            ? 'key'
                                                            : 'none'
                                                    }
                                                    keyOffset={
                                                        keyOffset
                                                    }
                                                    className={
                                                        meta?.cellClassName
                                                    }
                                                >
                                                    {flexRender(
                                                        cell.column
                                                            .columnDef
                                                            .cell,
                                                        cell.getContext(),
                                                    )}
                                                </StyledCell>
                                            )
                                        })}
                                </StyledRow>
                            ))}
                        </StyledTableBody>
                    </StyledTable>
                </DataTableScrollArea>
            ) : null}

            {pagination ? (
                <DataTablePagination
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    pageCount={pageCount}
                    totalRowCount={totalRowCount}
                    pageSizeOptions={
                        pagination.pageSizeOptions ??
                        DEFAULT_PAGE_SIZE_OPTIONS
                    }
                    onPageIndexChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                />
            ) : null}
        </div>
    )
}
